// Kalender und Geometrie der Zeitleiste. REINE Funktionen, kein DOM, kein Netz.
//
// Alles, was rechnet, steht hier – das Zeichnen liegt in Zeitleiste.tsx.
// Die einzige Ausnahme von „kein DOM“ ist das MESSEN von Text: Wie breit ein
// Wort wird, weiß nur der Browser. Deshalb wird die Messfunktion von außen
// hereingereicht (siehe messen.ts) statt hier aufgerufen – so bleiben diese
// Funktionen testbar.
//
// Herkunft: übernommen aus dem abgenommenen Entwurf
// (entwurf/zeitleiste-entwurf.html). Die dort teuer gefundenen Fallstricke
// stehen als Kommentar an der jeweiligen Stelle.

import type { Status } from "./planner";

export type Ansicht = "woche" | "monat" | "quartal";

/** Spaltenbreite in Pixeln bei Zoom 100 %. */
export const ANSICHT_BREITE: Record<Ansicht, number> = { woche: 64, monat: 96, quartal: 150 };

export const ZOOM_MIN = 0.4;
export const ZOOM_MAX = 2.4;
export const ZOOM_SCHRITT = 0.2;

/**
 * Nächste Zoomstufe im Raster, begrenzt auf [ZOOM_MIN, ZOOM_MAX].
 *
 * Das Einrasten ist keine Kosmetik: Ohne es verschiebt EIN Anschlag an der
 * Grenze das ganze Raster. Von 100 % zehnmal vergrößern klemmt bei der oberen
 * Grenze; geht man von dort mit 0,2er-Schritten zurück, kommt man bei 110 %,
 * 90 %, 70 % heraus – 100 % ist dann nicht mehr erreichbar. Genau das ist beim
 * Messen aufgefallen.
 */
export function zoomStufe(z: number): number {
  const geraster = Math.round(z / ZOOM_SCHRITT) * ZOOM_SCHRITT;
  const begrenzt = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, geraster));
  // Gegen Rundungsreste der Fließkommarechnung (0.2 + 0.1 = 0.30000000000000004).
  return Math.round(begrenzt * 100) / 100;
}

/** Wie viel Raddrehung (deltaY, Pixel) einen Zoomschritt auslöst. */
export const RAD_SCHWELLE = 40;

/**
 * Zoomen mit Strg/Cmd + Mausrad: Die Drehung wird gesammelt, ein Schritt fällt
 * erst ab `RAD_SCHWELLE`. Eine Mausrad-Raste liefert rund 100 px auf einmal –
 * das ist ein Schritt. Ein Trackpad (auch das Zusammenziehen mit zwei Fingern,
 * das der Browser als Strg + Rad meldet) liefert viele kleine Werte; ohne
 * Sammeln spränge der Zoom dort bei einer kleinen Geste ans Ende.
 * Rad nach unten (deltaY > 0) heißt kleiner, wie überall.
 */
export function radZoom(rest: number, deltaY: number): { rest: number; richtung: -1 | 0 | 1 } {
  const summe = rest + deltaY;
  if (Math.abs(summe) < RAD_SCHWELLE) return { rest: summe, richtung: 0 };
  return { rest: 0, richtung: summe > 0 ? -1 : 1 };
}

/** Ein Balken wird nie schmaler gezeichnet – sonst ist er nicht mehr zu treffen. */
export const MIND_BREITE = 6;

const MON = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];
const MON_LANG = [
  "Januar", "Februar", "März", "April", "Mai", "Juni",
  "Juli", "August", "September", "Oktober", "November", "Dezember",
];

// ── Tage ─────────────────────────────────────────────────────────────────────
// Kalendertage sind Zeichenketten JJJJ-MM-TT (so kommen sie aus planner.ts),
// gerechnet wird mit Date in ORTSZEIT. Nie mit UTC mischen: `new Date("2027-03-01")`
// wäre UTC-Mitternacht und in Deutschland der 28.02. um 23 Uhr.

export function tagAus(jjjjmmtt: string): Date {
  const [j, m, t] = jjjjmmtt.split("-").map(Number);
  return new Date(j, m - 1, t);
}

export function tagText(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** JJJJ-MM-TT → TT.MM.JJJJ */
export function lesbar(jjjjmmtt: string): string {
  const [j, m, t] = jjjjmmtt.split("-");
  return `${t}.${m}.${j}`;
}

/** JJJJ-MM-TT → TT.MM.JJ – für enge Spalten (Druck). */
export function lesbarKurz(jjjjmmtt: string): string {
  const [j, m, t] = jjjjmmtt.split("-");
  return `${t}.${m}.${j.slice(2)}`;
}

export function plusTage(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

/** Anzahl Kalendertage von start bis ende EINSCHLIESSLICH beider Tage. */
export function tageZwischen(start: string, ende: string): number {
  return Math.round((tagAus(ende).getTime() - tagAus(start).getTime()) / 864e5) + 1;
}

/** Kalenderwoche nach ISO 8601 (Woche mit dem ersten Donnerstag). */
export function kalenderwoche(d: Date): number {
  const x = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const wochentag = x.getUTCDay() || 7; // Sonntag = 7, nicht 0
  x.setUTCDate(x.getUTCDate() + 4 - wochentag); // auf den Donnerstag der Woche
  const jahresanfang = new Date(Date.UTC(x.getUTCFullYear(), 0, 1));
  return Math.ceil(((x.getTime() - jahresanfang.getTime()) / 864e5 + 1) / 7);
}

// ── Spalten ──────────────────────────────────────────────────────────────────

export type Spalte = {
  von: Date;
  /** Erster Tag NACH der Spalte (ausschließlich). */
  bis: Date;
  breite: number;
  /** Untere Kopfzeile: „38“, „Sep“, „Q3“. */
  text: string;
  /** Schlüssel der oberen Kopfzeile – gleiche Nachbarn werden zusammengefasst. */
  gruppe: string;
  /** Beschriftungen der oberen Zeile, von lang nach kurz. Letzte Wahl: leer. */
  varianten: string[];
};

export type Gitter = {
  spalten: Spalte[];
  anfang: Date;
  /** Ende ausschließlich. */
  ende: Date;
  gesamtBreite: number;
  /** Linke Kante jeder Spalte, plus Gesamtbreite als letzter Wert. */
  kanten: number[];
};

/**
 * Zeitraum, den die Zeitleiste umspannt: alle Aufgaben plus heute, aufgerundet
 * auf einen Monat Vorlauf und zwei Monate Nachlauf. Heute ist immer dabei,
 * damit die Heute-Linie nie außerhalb liegt.
 */
export function zeitraum(
  aufgaben: readonly { start: string; ende: string }[],
  heute: Date,
): { min: Date; max: Date } {
  let min = new Date(heute);
  let max = new Date(heute);
  for (const a of aufgaben) {
    const s = tagAus(a.start);
    const e = tagAus(a.ende);
    if (s < min) min = s;
    if (e > max) max = e;
  }
  return {
    min: new Date(min.getFullYear(), min.getMonth() - 1, 1),
    max: new Date(max.getFullYear(), max.getMonth() + 2, 1),
  };
}

/**
 * Baut die Spalten der gewählten Ansicht über [min, max).
 * Voraussetzung: min < max – das liefert `zeitraum` immer (mindestens drei Monate).
 */
export function gitterBauen(ansicht: Ansicht, zoom: number, min: Date, max: Date): Gitter {
  const breite = Math.round(ANSICHT_BREITE[ansicht] * zoom);
  const spalten: Spalte[] = [];

  if (ansicht === "woche") {
    // Auf den Montag am oder vor `min` zurück – Wochenspalten beginnen montags.
    let d = new Date(min);
    const wt = d.getDay();
    if (wt !== 1) d = plusTage(d, wt === 0 ? -6 : 1 - wt);
    while (d < max) {
      const m = d.getMonth();
      const j = d.getFullYear();
      spalten.push({
        von: new Date(d),
        bis: plusTage(d, 7),
        breite,
        text: String(kalenderwoche(d)),
        gruppe: `${MON_LANG[m]} ${j}`,
        varianten: [`${MON_LANG[m]} ${j}`, `${MON[m]} ${j}`, MON[m], ""],
      });
      d = plusTage(d, 7);
    }
  } else if (ansicht === "monat") {
    let d = new Date(min);
    while (d < max) {
      const bis = new Date(d.getFullYear(), d.getMonth() + 1, 1);
      const j = String(d.getFullYear());
      spalten.push({ von: new Date(d), bis, breite, text: MON[d.getMonth()], gruppe: j, varianten: [j, ""] });
      d = bis;
    }
  } else {
    let d = new Date(min.getFullYear(), Math.floor(min.getMonth() / 3) * 3, 1);
    while (d < max) {
      const bis = new Date(d.getFullYear(), d.getMonth() + 3, 1);
      const j = String(d.getFullYear());
      spalten.push({
        von: new Date(d),
        bis,
        breite,
        text: `Q${Math.floor(d.getMonth() / 3) + 1}`,
        gruppe: j,
        varianten: [j, ""],
      });
      d = bis;
    }
  }

  const kanten: number[] = [];
  let x = 0;
  for (const s of spalten) {
    kanten.push(x);
    x += s.breite;
  }
  kanten.push(x);

  return {
    spalten,
    anfang: spalten[0].von,
    ende: spalten[spalten.length - 1].bis,
    gesamtBreite: x,
    kanten,
  };
}

/**
 * x-Position eines Zeitpunkts in Pixeln.
 *
 * Gerechnet wird INNERHALB der Spalte, nicht linear über den ganzen Zeitraum.
 * Der Unterschied ist keine Kosmetik: Alle Spalten sind gleich breit, umfassen
 * aber unterschiedlich viele Tage (Februar 28, März 31; Q1 90, Q3 92). Über den
 * ganzen Zeitraum linear gerechnet, läge der Monatserste nicht auf der
 * Monatskante – ein Balken begänne sichtbar neben seiner Spalte.
 */
export function xAm(gitter: Gitter, d: Date): number {
  const z = d.getTime();
  if (z <= gitter.anfang.getTime()) return 0;
  if (z >= gitter.ende.getTime()) return gitter.gesamtBreite;
  for (let i = 0; i < gitter.spalten.length; i++) {
    const s = gitter.spalten[i];
    if (z < s.bis.getTime()) {
      const anteil = (z - s.von.getTime()) / (s.bis.getTime() - s.von.getTime());
      return gitter.kanten[i] + anteil * s.breite;
    }
  }
  return gitter.gesamtBreite;
}

export function xAmTag(gitter: Gitter, jjjjmmtt: string): number {
  return xAm(gitter, tagAus(jjjjmmtt));
}

/**
 * Breite eines Balkens von start bis ende. Der Endtag zählt MIT – gezeichnet
 * wird bis zum Beginn des Folgetags, sonst fehlte dem letzten Tag seine Fläche.
 */
export function balkenBreite(gitter: Gitter, start: string, ende: string): number {
  const links = xAmTag(gitter, start);
  const rechts = xAm(gitter, plusTage(tagAus(ende), 1));
  return Math.max(rechts - links, MIND_BREITE);
}

// ── Kopfzeile ────────────────────────────────────────────────────────────────

export type KopfGruppe = { varianten: string[]; breite: number; istHeute: boolean };

/** Benachbarte Spalten mit gleichem Schlüssel zu einem Kopfeintrag zusammenfassen. */
export function kopfGruppen(spalten: readonly Spalte[], heute: Date): KopfGruppe[] {
  const raus: KopfGruppe[] = [];
  let schluessel: string | null = null;
  for (const c of spalten) {
    if (c.gruppe !== schluessel) {
      schluessel = c.gruppe;
      raus.push({ varianten: c.varianten, breite: 0, istHeute: false });
    }
    const letzte = raus[raus.length - 1];
    letzte.breite += c.breite;
    if (istInSpalte(c, heute)) letzte.istHeute = true;
  }
  return raus;
}

export function istInSpalte(spalte: Spalte, d: Date): boolean {
  return d >= spalte.von && d < spalte.bis;
}

/**
 * Erste Beschriftung nehmen, die in die Breite passt („November 2025“ →
 * „Nov 2025“ → „Nov“ → „“). Gemessen wird, nicht geschätzt: Eine geschätzte
 * Zeichenbreite von 6,2 px war im Entwurf die Ursache abgeschnittener Köpfe.
 */
export function passendeBeschriftung(
  varianten: readonly string[],
  breite: number,
  messen: (text: string) => number,
): string {
  for (const v of varianten) if (!v || messen(v) <= breite - 16) return v;
  return "";
}

// ── Termine-Zeile ────────────────────────────────────────────────────────────

/** Zeilenhöhe einer Termin-Spur. */
export const TERMIN_ZEILE = 24;
/** Seitenlänge der Raute. EINZIGE Quelle – auch die CSS-Größe kommt von hier. */
export const RAUTE_SEITE = 12;
/**
 * Sichtbare Breite der Raute. Sie ist ein um 45° gedrehtes Quadrat – sichtbar
 * ist damit ihre DIAGONALE (12 × √2 ≈ 17), nicht die Seitenlänge. Mit der
 * Seitenlänge gerechnet stießen im Entwurf Beschriftungen um 2–3 px aneinander.
 */
export const RAUTE_BREITE = Math.ceil(RAUTE_SEITE * Math.SQRT2);
/** Mindestabstand zwischen zwei Einträgen derselben Spur. */
export const TERMIN_LUFT = 10;

export type Termin = { id: string; titel: string; start: string; ende: string; status: Status };

export type TerminPlatz = {
  id: string;
  spur: number;
  /** Einzeltermin: wird als Raute gezeichnet, nicht als Balken. */
  punkt: boolean;
  links: number;
  breite: number;
  /** Der Titel passt in den Balken und steht darin. */
  innen: boolean;
  /** x des Titels, wenn er neben dem Balken steht. */
  textLinks: number | null;
};

/**
 * Termine teilen sich eine Zeile. Was sich überschneidet, rutscht eine Spur
 * tiefer – MITSAMT seiner Beschriftung, sonst legt sich der Text des einen auf
 * den Balken des nächsten.
 */
export function termineAnordnen(
  termine: readonly Termin[],
  gitter: Gitter,
  messen: (text: string) => number,
): { plaetze: TerminPlatz[]; spuren: number } {
  const sortiert = [...termine].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  /** Rechter Rand des letzten Eintrags je Spur. */
  const raender: number[] = [];
  const plaetze: TerminPlatz[] = [];

  for (const t of sortiert) {
    const punkt = t.start === t.ende;
    const x0 = xAmTag(gitter, t.start);
    const breite = punkt ? RAUTE_BREITE : balkenBreite(gitter, t.start, t.ende);
    const links = punkt ? x0 - RAUTE_BREITE / 2 : x0;
    const textBreite = messen(t.titel);

    // Passt der Text in den Balken, steht er darin – das spart Platz und kann
    // mit nichts kollidieren.
    const innen = !punkt && breite >= textBreite + 16;
    let textLinks: number | null = null;
    let von = links;
    let bis = links + breite;
    if (!innen) {
      textLinks = links + breite + 6;
      if (textLinks + textBreite > gitter.gesamtBreite) {
        // Rechts kein Platz mehr: Text nach links, der Eintrag beginnt dort.
        textLinks = links - 6 - textBreite;
        von = textLinks;
      } else {
        bis = textLinks + textBreite;
      }
    }

    let spur = raender.findIndex((rand) => rand + TERMIN_LUFT < von);
    if (spur < 0) {
      spur = raender.length;
      raender.push(0);
    }
    raender[spur] = bis;
    plaetze.push({ id: t.id, spur, punkt, links, breite, innen, textLinks });
  }

  return { plaetze, spuren: Math.max(1, raender.length) };
}

/** Höhe der Termine-Zeile für die gegebene Anzahl Spuren. */
export function termineHoehe(spuren: number): number {
  return Math.max(1, spuren) * TERMIN_ZEILE + 10;
}

/** Fällig, nicht erledigt, Endtag vorbei. */
export function istUeberfaellig(a: { ende: string; status: Status }, heuteText: string): boolean {
  return a.status !== "erledigt" && a.ende < heuteText;
}
